import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ArtistApplication, ProfileResponse } from '../types';
import { validateFileSize } from '../constants/fileUpload';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ArtistApplication | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    displayName: '',
    bio: '',
    targetPricePoint: '',
    instagram: '',
    website: '',
    otherSocial: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasArtistProfile, setHasArtistProfile] = useState(false);
  const { success, error: showError } = useToast();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [exportingData, setExportingData] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [formData, setFormData] = useState({
    displayName: user?.firstName || user?.email || '',
    statement: '',
    targetPricePoint: '',
    instagram: '',
    website: '',
    otherSocial: '',
    attachments: [] as File[]
  });

  useEffect(() => {
    const fetchApplication = async (): Promise<void> => {
      try {
        const response = await api.get<ArtistApplication>('/artist-applications/me');
        setApplication(response.data);
        setFormData(prev => ({
          ...prev,
          displayName: response.data.displayName || prev.displayName,
          statement: response.data.statement || prev.statement,
          targetPricePoint: response.data.targetPricePoint || prev.targetPricePoint,
          instagram: response.data.instagram || prev.instagram,
          website: response.data.website || prev.website,
          otherSocial: response.data.otherSocial || prev.otherSocial
        }));
      } catch {
        setApplication(null);
      } finally {
        setLoading(false);
      }
    };
    const fetchProfile = async (): Promise<void> => {
      try {
        const response = await api.get<ProfileResponse>('/profile/me');
        const profile = response.data.artistProfile;
        setHasArtistProfile(!!profile);
        setProfileForm({
          firstName: response.data.user.firstName || '',
          lastName: response.data.user.lastName || '',
          displayName: profile?.displayName || '',
          bio: profile?.bio || '',
          targetPricePoint: profile?.targetPricePoint || '',
          instagram: profile?.instagram || '',
          website: profile?.website || '',
          otherSocial: profile?.otherSocial || ''
        });
      } catch (error) {
        console.error('Failed to load profile', error);
      }
    };
    fetchProfile();
    fetchApplication();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    
    // Validate file sizes using centralized validation
    const validFiles: File[] = [];
    
    for (const file of files) {
      const validation = validateFileSize(file);
      if (!validation.isValid) {
        showError(`File "${file.name}": ${validation.error || 'Invalid file size'}`);
        continue;
      }
      validFiles.push(file);
    }
    
    setFormData(prev => ({ ...prev, attachments: validFiles }));
  };

  const submitApplication = async (): Promise<void> => {
    setSubmitting(true);
    try {
      if (!formData.displayName.trim() || !formData.statement.trim()) {
        showError('Display name and statement are required.');
        return;
      }

      const payload = new FormData();
      payload.append('DisplayName', formData.displayName);
      payload.append('Statement', formData.statement);
      if (formData.targetPricePoint) payload.append('TargetPricePoint', formData.targetPricePoint);
      if (formData.instagram) payload.append('Instagram', formData.instagram);
      if (formData.website) payload.append('Website', formData.website);
      if (formData.otherSocial) payload.append('OtherSocial', formData.otherSocial);
      formData.attachments.forEach((file) => payload.append('Attachments', file));

      const response = await api.post<ArtistApplication>('/artist-applications', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setApplication(response.data);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const saveProfile = async (): Promise<void> => {
    setSavingProfile(true);
    try {
      await api.put('/profile/me', {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        displayName: profileForm.displayName,
        bio: profileForm.bio,
        targetPricePoint: profileForm.targetPricePoint,
        instagram: profileForm.instagram,
        website: profileForm.website,
        otherSocial: profileForm.otherSocial
      });
      success('Profile updated');
    } catch (error) {
      showError('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const exportMyData = async (): Promise<void> => {
    setExportingData(true);
    try {
      const response = await api.get('/profile/me/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'phi-printing-my-data.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showError('Failed to export your data. Please try again.');
    } finally {
      setExportingData(false);
    }
  };

  const deleteAccount = async (): Promise<void> => {
    if (!deletePassword) {
      showError('Please enter your password to confirm deletion.');
      return;
    }
    setDeletingAccount(true);
    try {
      await api.post('/profile/me/delete', { password: deletePassword });
      success('Your account has been deleted.');
      logout();
      navigate('/');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const updateApplicationInfo = async (): Promise<void> => {
    if (!application) return;
    try {
      const response = await api.put<ArtistApplication>('/artist-applications/me', {
        displayName: formData.displayName,
        statement: formData.statement,
        targetPricePoint: formData.targetPricePoint,
        instagram: formData.instagram,
        website: formData.website,
        otherSocial: formData.otherSocial
      });
      setApplication(response.data);
      success('Application updated');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to update application');
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  const isArtist = hasArtistProfile || user?.role === 'Artist';

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-page-header">
          <h1>My Profile</h1>
          {isArtist && (
            <span className="profile-badge profile-badge--artist" title="You have an artist profile">
              <i className="fas fa-palette"></i> Artist
            </span>
          )}
        </div>

        <div className="profile-card">
          <h2>Account Details</h2>
          <p><strong>Email:</strong> {user?.email}</p>
          <p><strong>Role:</strong> {user?.role}</p>
          <p>
            <strong>Loyalty points:</strong> {user?.loyaltyPoints ?? 0}
            {' '}
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9em' }}>
              (1 point per $1 spent · 100 points = $5 off at checkout)
            </span>
          </p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="profile-first-name">First Name</label>
              <input
                id="profile-first-name"
                type="text"
                value={profileForm.firstName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="profile-last-name">Last Name</label>
              <input
                id="profile-last-name"
                type="text"
                value={profileForm.lastName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="profile-display-name">Artist Display Name</label>
            <input
              id="profile-display-name"
              type="text"
              value={profileForm.displayName}
              onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="profile-bio">Artist Bio</label>
            <textarea
              id="profile-bio"
              rows={3}
              value={profileForm.bio}
              onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="profile-target-price-point">Target Price Point</label>
              <input
                id="profile-target-price-point"
                type="text"
                value={profileForm.targetPricePoint}
                onChange={(e) => setProfileForm(prev => ({ ...prev, targetPricePoint: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="profile-instagram">Instagram</label>
              <input
                id="profile-instagram"
                type="text"
                value={profileForm.instagram}
                onChange={(e) => setProfileForm(prev => ({ ...prev, instagram: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="profile-website">Website</label>
              <input
                id="profile-website"
                type="text"
                value={profileForm.website}
                onChange={(e) => setProfileForm(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="profile-other-social">Other Social</label>
              <input
                id="profile-other-social"
                type="text"
                value={profileForm.otherSocial}
                onChange={(e) => setProfileForm(prev => ({ ...prev, otherSocial: e.target.value }))}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
          {isArtist && (
            <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
              <Link to="/artist/storefront" className="btn btn-secondary">
                <i className="fas fa-paint-brush"></i> Customize Storefront
              </Link>
              <Link to="/artist/sales" className="btn btn-secondary">
                <i className="fas fa-tags"></i> Manage My Sales
              </Link>
            </div>
          )}
        </div>

        <div className="profile-card">
          <h2>Change Password</h2>
          {!showPasswordChange ? (
            <button 
              className="btn btn-outline" 
              onClick={() => setShowPasswordChange(true)}
            >
              Change Password
            </button>
          ) : (
            <div className="password-change-form">
              <div className="form-group">
                <label htmlFor="profile-current-password">Current Password</label>
                <input
                  id="profile-current-password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="profile-new-password">New Password</label>
                <input
                  id="profile-new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label htmlFor="profile-confirm-password">Confirm New Password</label>
                <input
                  id="profile-confirm-password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  minLength={6}
                />
              </div>
              <div className="form-actions" style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
                      showError('Please fill in all password fields');
                      return;
                    }
                    if (passwordForm.newPassword.length < 6) {
                      showError('New password must be at least 6 characters long');
                      return;
                    }
                    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                      showError('New password and confirmation do not match');
                      return;
                    }
                    setChangingPassword(true);
                    try {
                      await api.post('/profile/me/change-password', {
                        currentPassword: passwordForm.currentPassword,
                        newPassword: passwordForm.newPassword,
                        confirmPassword: passwordForm.confirmPassword
                      });
                      success('Password changed successfully');
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setShowPasswordChange(false);
                    } catch (error: any) {
                      showError(error.response?.data?.message || 'Failed to change password');
                    } finally {
                      setChangingPassword(false);
                    }
                  }}
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  disabled={changingPassword}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {!hasArtistProfile && (
        <div className="profile-card">
          <h2>Apply to be a Phi Artist</h2>
          <p>
            <Link to="/become-an-artist">View tier benefits and how to apply</Link>
          </p>
          {application ? (
            <div className={`application-status status-${application.status.toLowerCase()}`}>
              <p><strong>Status:</strong> {application.status}</p>
              {application.adminNotes && (
                <p><strong>Admin Notes:</strong> {application.adminNotes}</p>
              )}
              <p><strong>Submitted:</strong> {new Date(application.createdAt).toLocaleDateString()}</p>
            </div>
          ) : (
            <p>No application submitted yet.</p>
          )}

          {(!application || application.status.toLowerCase() === 'rejected') && (
            <div className="artist-application-form">
              <div className="form-group">
                <label htmlFor="application-display-name">Display Name</label>
                <input
                  id="application-display-name"
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="application-statement">Why do you want to host your work here?</label>
                <textarea
                  id="application-statement"
                  value={formData.statement}
                  onChange={(e) => setFormData(prev => ({ ...prev, statement: e.target.value }))}
                  rows={4}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="application-target-price-point">Target Price Point</label>
                  <input
                    id="application-target-price-point"
                    type="text"
                    value={formData.targetPricePoint}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetPricePoint: e.target.value }))}
                    placeholder="e.g. $50-$200"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="application-instagram">Instagram</label>
                  <input
                    id="application-instagram"
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                    placeholder="@handle"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="application-website">Website</label>
                  <input
                    id="application-website"
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="application-other-social">Other Social</label>
                  <input
                    id="application-other-social"
                    type="text"
                    value={formData.otherSocial}
                    onChange={(e) => setFormData(prev => ({ ...prev, otherSocial: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="application-attachments">Supporting Materials</label>
                <input id="application-attachments" type="file" multiple onChange={handleFileChange} />
                <small>Upload a few supporting files (images, PDFs, etc.).</small>
              </div>
              <button className="btn btn-primary" disabled={submitting} onClick={submitApplication}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          )}

          {application && application.status.toLowerCase() !== 'approved' && (
            <button className="btn btn-outline" onClick={updateApplicationInfo}>
              Update Application Info
            </button>
          )}
        </div>
        )}

        <div className="profile-card">
          <h2>Privacy &amp; Data</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            Download a copy of everything we store about you, or permanently
            delete your account. See our <Link to="/privacy">Privacy Policy</Link>{' '}
            for details on what we keep and why.
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
            <button className="btn btn-outline" onClick={exportMyData} disabled={exportingData}>
              <i className="fas fa-download"></i>{' '}
              {exportingData ? 'Preparing...' : 'Download My Data'}
            </button>
            {!showDeleteAccount && (
              <button
                className="btn btn-outline"
                style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                onClick={() => setShowDeleteAccount(true)}
              >
                <i className="fas fa-trash"></i> Delete My Account
              </button>
            )}
          </div>
          {showDeleteAccount && (
            <div style={{ marginTop: 'var(--spacing-lg)' }}>
              <p style={{ color: 'var(--color-danger)', fontWeight: 600 }}>
                This permanently deletes your account, addresses, and artist
                storefront. It cannot be undone. Order records are kept without
                your identity, as required for tax purposes.
              </p>
              <div className="form-group" style={{ maxWidth: '320px' }}>
                <label htmlFor="profile-delete-password">Enter your password to confirm</label>
                <input
                  id="profile-delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button
                  className="btn btn-primary"
                  style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                  onClick={deleteAccount}
                  disabled={deletingAccount}
                >
                  {deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteAccount(false);
                    setDeletePassword('');
                  }}
                  disabled={deletingAccount}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
